import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { useSports } from '../../../../queries';
import { useCasinoCategories } from '../../../../queries/casino';

export const MAX_ITEMS = 15;

type SectionKey = 'casino' | 'sportsbook';

export const getSelectedSportIdFromPath = (pathname: string) => {
  const [, sportId = ''] = pathname.match(/^\/sports\/([^/]+)/) ?? [];

  return decodeURIComponent(sportId);
};

export const getDefaultExpandedSection = ({
  pathname,
  hasCasinoAccess,
  hasSportsbookAccess,
}: {
  pathname: string;
  hasCasinoAccess: boolean;
  hasSportsbookAccess: boolean;
}): SectionKey | null => {
  if (pathname.startsWith('/promotions')) return null;
  if (hasCasinoAccess && !hasSportsbookAccess) return 'casino';
  if (hasSportsbookAccess && !hasCasinoAccess) return 'sportsbook';
  if (hasCasinoAccess && pathname.startsWith('/casino')) return 'casino';
  if (hasSportsbookAccess) return 'sportsbook';
  if (hasCasinoAccess) return 'casino';

  return null;
};

const getVisibleItems = <T>({
  items,
  showAll,
  selectedId,
  getId,
}: {
  items: T[];
  showAll: boolean;
  selectedId: string;
  getId: (item: T) => string;
}) => {
  if (showAll) return items;

  const firstItems = items.slice(0, MAX_ITEMS);
  const selectedIsVisible = !selectedId || firstItems.some((item) => getId(item) === selectedId);

  if (selectedIsVisible) return firstItems;

  const selectedItem = items.find((item) => getId(item) === selectedId);

  return selectedItem ? [...firstItems, selectedItem] : firstItems;
};

export type { SectionKey };

export const useBettingDrawerSectionState = ({
  pathname,
  hasCasinoAccess,
  hasSportsbookAccess,
}: {
  pathname: string;
  hasCasinoAccess: boolean;
  hasSportsbookAccess: boolean;
}) => {
  const defaultExpandedSection = useMemo(
    () =>
      getDefaultExpandedSection({
        pathname,
        hasCasinoAccess,
        hasSportsbookAccess,
      }),
    [hasCasinoAccess, hasSportsbookAccess, pathname]
  );
  const [expandedSection, setExpandedSection] = useState<SectionKey | null>(defaultExpandedSection);

  useEffect(() => {
    setExpandedSection(defaultExpandedSection);
  }, [defaultExpandedSection]);

  const isSectionExpanded = useCallback((sectionKey: SectionKey) => expandedSection === sectionKey, [expandedSection]);

  const toggleExpandedSection = useCallback((sectionKey: SectionKey) => {
    setExpandedSection((currentSection) => (currentSection === sectionKey ? null : sectionKey));
  }, []);

  const activateSection = useCallback((sectionKey: SectionKey) => {
    setExpandedSection(sectionKey);
  }, []);

  return {
    expandedSection,
    setExpandedSection,
    isSectionExpanded,
    toggleExpandedSection,
    activateSection,
  };
};

const useBettingDrawerData = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllSports, setShowAllSports] = useState(false);
  const { t } = useLocalization();
  const { hasSportsbookAccess, hasCasinoAccess } = useProductAvailability();

  const selectedCategoryId = searchParams.get('category') ?? '';
  const { expandedSection, setExpandedSection, isSectionExpanded, toggleExpandedSection, activateSection } =
    useBettingDrawerSectionState({
      pathname: location.pathname,
      hasCasinoAccess,
      hasSportsbookAccess,
    });
  const {
    data: sports = [],
    isLoading: isSportsLoading,
    isFetching: isSportsFetching,
  } = useSports({
    enabled: hasSportsbookAccess,
    queryParams: {
      isLive: false,
    },
  });
  const { data: categories = [], isLoading: isCategoriesLoading } = useCasinoCategories({ enabled: hasCasinoAccess });

  const selectedSportId = getSelectedSportIdFromPath(location.pathname);

  const visibleCategories = useMemo(
    () =>
      getVisibleItems({
        items: categories,
        showAll: showAllCategories,
        selectedId: selectedCategoryId,
        getId: (category) => category._id,
      }),
    [categories, selectedCategoryId, showAllCategories]
  );
  const visibleSports = useMemo(
    () =>
      getVisibleItems({
        items: sports,
        showAll: showAllSports,
        selectedId: selectedSportId,
        getId: (sport) => sport.id,
      }),
    [selectedSportId, showAllSports, sports]
  );

  const showShowAllCategories = categories.length > visibleCategories.length && !showAllCategories;
  const showShowLessCategories = categories.length > MAX_ITEMS && showAllCategories;
  const showShowAllSports = sports.length > visibleSports.length && !showAllSports;
  const showShowLessSports = sports.length > MAX_ITEMS && showAllSports;

  return {
    location,
    navigate,
    t,
    hasSportsbookAccess,
    hasCasinoAccess,
    expandedSection,
    setExpandedSection,
    isSectionExpanded,
    toggleExpandedSection,
    activateSection,
    sports,
    isSportsLoading,
    isSportsFetching,
    categories,
    isCategoriesLoading,
    selectedCategoryId,
    selectedSportId,
    visibleCategories,
    visibleSports,
    showShowAllCategories,
    showShowLessCategories,
    showShowAllSports,
    showShowLessSports,
    setShowAllCategories,
    setShowAllSports,
  };
};

export type BettingDrawerData = ReturnType<typeof useBettingDrawerData>;

export default useBettingDrawerData;
